import { Head, router, useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface TimeEntryData {
  user_id: number | null
  time_mode: "actual" | "block"
  date: string | null
  started_at: string | null
  ended_at: string | null
  admin_minutes: number
  service_item_type_id: number | null
  travel_hours: number
  notes: string
  mileage_to_job: number
  mileage_on_job: number
  mileage_from_job: number
  vehicle_allowance: string
}

interface TravelHourOption {
  value: number
  label: string
}

interface Props {
  customer: Customer
  time_entry: TimeEntryData
  users: { id: number; name: string }[]
  service_item_types: { id: number; name: string }[]
  travel_hour_options: TravelHourOption[]
  can_select_employee: boolean
  can_use_block_time: boolean
  errors?: Record<string, string[]>
}

// Build hour options for block time
const blockHourOptions = Array.from({ length: 25 }, (_, i) => i)
const blockMinuteOptions = [0, 15, 30, 45]

// Build hour/minute options for actual time
const clockHourOptions = Array.from({ length: 12 }, (_, i) => i + 1)
const clockMinuteOptions = [0, 15, 30, 45]

export default function New({
  customer,
  time_entry,
  users,
  service_item_types,
  travel_hour_options,
  can_select_employee,
  can_use_block_time,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Time Entries", href: `/customers/${customer.id}/time_entries` },
    { title: "New", href: `/customers/${customer.id}/time_entries/new` },
  ]

  const [timeMode, setTimeMode] = useState<"actual" | "block">(
    time_entry.time_mode ?? "actual",
  )

  // Actual mode state
  const [startDate, setStartDate] = useState(time_entry.date ?? "")
  const [startHour, setStartHour] = useState(8)
  const [startMinute, setStartMinute] = useState(0)
  const [startAmPm, setStartAmPm] = useState<"AM" | "PM">("AM")
  const [endDate, setEndDate] = useState(time_entry.date ?? "")
  const [endHour, setEndHour] = useState(5)
  const [endMinute, setEndMinute] = useState(0)
  const [endAmPm, setEndAmPm] = useState<"AM" | "PM">("PM")

  // Block mode state
  const [blockDate, setBlockDate] = useState(time_entry.date ?? "")
  const [blockHours, setBlockHours] = useState(0)
  const [blockMinutes, setBlockMinutes] = useState(0)

  const { data, setData, post, processing, transform } = useForm<TimeEntryData>(
    {
      user_id: time_entry.user_id,
      time_mode: time_entry.time_mode ?? "actual",
      date: time_entry.date,
      started_at: time_entry.started_at,
      ended_at: time_entry.ended_at,
      admin_minutes: time_entry.admin_minutes ?? 0,
      service_item_type_id: time_entry.service_item_type_id,
      travel_hours: time_entry.travel_hours ?? 0,
      notes: time_entry.notes ?? "",
      mileage_to_job: time_entry.mileage_to_job ?? 0,
      mileage_on_job: time_entry.mileage_on_job ?? 0,
      mileage_from_job: time_entry.mileage_from_job ?? 0,
      vehicle_allowance: time_entry.vehicle_allowance || "",
    },
  )

  // Build datetime from date + hour + minute + ampm
  function buildDateTime(
    date: string,
    hour: number,
    minute: number,
    ampm: "AM" | "PM",
  ): string {
    let h24 = hour
    if (ampm === "AM" && hour === 12) h24 = 0
    else if (ampm === "PM" && hour !== 12) h24 += 12
    const hStr = h24.toString().padStart(2, "0")
    const mStr = minute.toString().padStart(2, "0")
    return `${date}T${hStr}:${mStr}:00`
  }

  const submit: FormEventHandler = (e) => {
    e.preventDefault()

    transform((formData) => {
      if (timeMode === "actual") {
        const startDt = buildDateTime(
          startDate,
          startHour,
          startMinute,
          startAmPm,
        )
        const endDt = buildDateTime(endDate, endHour, endMinute, endAmPm)
        return {
          case_time_entry: {
            ...formData,
            started_at: startDt,
            ended_at: endDt,
            admin_minutes: 0,
            date: startDate,
            time_mode: timeMode,
          },
        }
      } else {
        // Block mode
        return {
          case_time_entry: {
            ...formData,
            started_at: `${blockDate}T00:00:00`,
            ended_at: null,
            admin_minutes: blockHours * 60 + blockMinutes,
            date: blockDate,
            time_mode: timeMode,
          },
        }
      }
    })

    post(`/customers/${customer.id}/time_entries`)
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"
  const selectSmClass =
    "rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue text-sm"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Add Time Entry - ${customer.display_name}`} />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Add Time Entry</h1>

        <div className="rounded-lg bg-white p-6 shadow">
          <form onSubmit={submit} className="space-y-6">
            {/* Employee selector */}
            {can_select_employee && (
              <div>
                <label className={labelClass}>Employee</label>
                <select
                  className={inputClass}
                  value={data.user_id ?? ""}
                  onChange={(e) =>
                    setData(
                      "user_id",
                      e.target.value ? parseInt(e.target.value) : null,
                    )
                  }
                  required
                >
                  <option value="">Select employee...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
                {errors?.user_id && (
                  <p className={errorClass}>{errors.user_id[0]}</p>
                )}
              </div>
            )}

            {/* Time Type toggle */}
            <div>
              <label className={labelClass}>Time Type</label>
              <div className="mt-2 flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="time_mode"
                    value="actual"
                    checked={timeMode === "actual"}
                    onChange={() => setTimeMode("actual")}
                    className="text-hpi-active-blue focus:ring-hpi-active-blue"
                  />
                  <span className="text-sm">Actual (Clock Times)</span>
                </label>
                {can_use_block_time && (
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="time_mode"
                      value="block"
                      checked={timeMode === "block"}
                      onChange={() => setTimeMode("block")}
                      className="text-hpi-active-blue focus:ring-hpi-active-blue"
                    />
                    <span className="text-sm">Block (Duration)</span>
                  </label>
                )}
              </div>
            </div>

            {/* Actual Time Mode */}
            {timeMode === "actual" && (
              <div className="border-hpi-light-gray space-y-4 rounded border p-4">
                <h3 className="text-sm font-medium">Start Time</h3>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-hpi-med-gray mb-1 block text-xs">
                      Date
                    </label>
                    <input
                      type="date"
                      className={selectSmClass + " px-2 py-1"}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-hpi-med-gray mb-1 block text-xs">
                      Hour
                    </label>
                    <select
                      className={selectSmClass}
                      value={startHour}
                      onChange={(e) => setStartHour(parseInt(e.target.value))}
                    >
                      {clockHourOptions.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-hpi-med-gray mb-1 block text-xs">
                      Min
                    </label>
                    <select
                      className={selectSmClass}
                      value={startMinute}
                      onChange={(e) => setStartMinute(parseInt(e.target.value))}
                    >
                      {clockMinuteOptions.map((m) => (
                        <option key={m} value={m}>
                          {m.toString().padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select
                      className={selectSmClass}
                      value={startAmPm}
                      onChange={(e) =>
                        setStartAmPm(e.target.value as "AM" | "PM")
                      }
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>

                <h3 className="text-sm font-medium">End Time</h3>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-hpi-med-gray mb-1 block text-xs">
                      Date
                    </label>
                    <input
                      type="date"
                      className={selectSmClass + " px-2 py-1"}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-hpi-med-gray mb-1 block text-xs">
                      Hour
                    </label>
                    <select
                      className={selectSmClass}
                      value={endHour}
                      onChange={(e) => setEndHour(parseInt(e.target.value))}
                    >
                      {clockHourOptions.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-hpi-med-gray mb-1 block text-xs">
                      Min
                    </label>
                    <select
                      className={selectSmClass}
                      value={endMinute}
                      onChange={(e) => setEndMinute(parseInt(e.target.value))}
                    >
                      {clockMinuteOptions.map((m) => (
                        <option key={m} value={m}>
                          {m.toString().padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select
                      className={selectSmClass}
                      value={endAmPm}
                      onChange={(e) =>
                        setEndAmPm(e.target.value as "AM" | "PM")
                      }
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
                {errors?.started_at && (
                  <p className={errorClass}>{errors.started_at[0]}</p>
                )}
                {errors?.ended_at && (
                  <p className={errorClass}>{errors.ended_at[0]}</p>
                )}
              </div>
            )}

            {/* Block Time Mode */}
            {timeMode === "block" && (
              <div className="border-hpi-light-gray space-y-4 rounded border p-4">
                <h3 className="text-sm font-medium">Block Duration</h3>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-hpi-med-gray mb-1 block text-xs">
                      Date
                    </label>
                    <input
                      type="date"
                      className={selectSmClass + " px-2 py-1"}
                      value={blockDate}
                      onChange={(e) => setBlockDate(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-hpi-med-gray mb-1 block text-xs">
                      Hours
                    </label>
                    <select
                      className={selectSmClass}
                      value={blockHours}
                      onChange={(e) => setBlockHours(parseInt(e.target.value))}
                    >
                      {blockHourOptions.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-hpi-med-gray mb-1 block text-xs">
                      Minutes
                    </label>
                    <select
                      className={selectSmClass}
                      value={blockMinutes}
                      onChange={(e) =>
                        setBlockMinutes(parseInt(e.target.value))
                      }
                    >
                      {blockMinuteOptions.map((m) => (
                        <option key={m} value={m}>
                          {m.toString().padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {errors?.admin_minutes && (
                  <p className={errorClass}>{errors.admin_minutes[0]}</p>
                )}
              </div>
            )}

            <div className="grid gap-6 md:grid-cols-2">
              {/* Service Type */}
              <div>
                <label className={labelClass}>Service Type</label>
                <select
                  className={inputClass}
                  value={data.service_item_type_id ?? ""}
                  onChange={(e) =>
                    setData(
                      "service_item_type_id",
                      e.target.value ? parseInt(e.target.value) : null,
                    )
                  }
                  required
                >
                  <option disabled value="">
                    --- SELECT ---
                  </option>
                  {service_item_types.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name}
                    </option>
                  ))}
                </select>
                {errors?.service_item_type_id && (
                  <p className={errorClass}>{errors.service_item_type_id[0]}</p>
                )}
              </div>

              {/* Travel Time */}
              <div>
                <label className={labelClass}>Travel Time</label>
                <select
                  className={inputClass}
                  value={data.travel_hours}
                  onChange={(e) =>
                    setData("travel_hours", parseFloat(e.target.value))
                  }
                >
                  {travel_hour_options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className={labelClass}>Notes</label>
              <textarea
                className={inputClass}
                rows={4}
                value={data.notes}
                onChange={(e) => setData("notes", e.target.value)}
                required
              />
              {errors?.notes && <p className={errorClass}>{errors.notes[0]}</p>}
            </div>

            {/* Mileage section */}
            <div>
              <h3 className="text-hpi-dark-gray mb-3 text-sm font-medium">
                Mileage (optional)
              </h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="text-hpi-med-gray mb-1 block text-xs">
                    Miles to Job
                  </label>
                  <input
                    type="number"
                    min="0"
                    className={inputClass}
                    value={data.mileage_to_job || ""}
                    onChange={(e) =>
                      setData("mileage_to_job", parseInt(e.target.value) || 0)
                    }
                  />
                </div>
                <div>
                  <label className="text-hpi-med-gray mb-1 block text-xs">
                    Miles OTJ
                  </label>
                  <input
                    type="number"
                    min="0"
                    className={inputClass}
                    value={data.mileage_on_job || ""}
                    onChange={(e) =>
                      setData("mileage_on_job", parseInt(e.target.value) || 0)
                    }
                  />
                </div>
                <div>
                  <label className="text-hpi-med-gray mb-1 block text-xs">
                    Miles from Job
                  </label>
                  <input
                    type="number"
                    min="0"
                    className={inputClass}
                    value={data.mileage_from_job || ""}
                    onChange={(e) =>
                      setData("mileage_from_job", parseInt(e.target.value) || 0)
                    }
                  />
                </div>
                <div>
                  <label className="text-hpi-med-gray mb-1 block text-xs">
                    Mileage Reimbursement
                  </label>
                  <select
                    className={inputClass}
                    value={data.vehicle_allowance}
                    onChange={(e) =>
                      setData("vehicle_allowance", e.target.value)
                    }
                    required
                  >
                    <option disabled value="">
                      --- SELECT ---
                    </option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors?.vehicle_allowance && (
                    <p className={errorClass}>{errors.vehicle_allowance[0]}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Form actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() =>
                  router.get(`/customers/${customer.id}/time_entries`)
                }
                className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {processing ? "Saving..." : "Add Time Entry"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
